import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { Coupon, CouponBulkUpdatePayload } from '../../../models/coupon';
import { CouponService } from '../../../services/coupon.service';
import { AdminPageHeaderComponent } from '../../shared/admin-page-header/admin-page-header.component';

type TriState = boolean | 'mixed';
type BooleanField = 'is_active' | 'one_time_use' | 'trusted_only';
type ValueField = 'coupons_max_usage' | 'account_id' | 'expiration_datetime' | 'percentage_discount' | 'fixed_discount';

@Component({
  selector: 'app-bulk-coupon-edit',
  imports: [CommonModule, FormsModule, RouterModule, AdminPageHeaderComponent],
  templateUrl: './bulk-coupon-edit.component.html',
  styleUrl: './bulk-coupon-edit.component.scss',
})
export class BulkCouponEditComponent implements OnInit {
  coupons: Coupon[] = [];
  isLoading = true;
  isSaving = false;
  feedbackError = '';
  booleanValues: Record<BooleanField, TriState> = { is_active: 'mixed', one_time_use: 'mixed', trusted_only: 'mixed' };
  bulkEdit = { coupons_max_usage: null as number | null, account_id: null as number | null, expiration_datetime: '', percentage_discount: null as number | null, fixed_discount: null as number | null };
  hasDifferentValues: Record<ValueField, boolean> = { coupons_max_usage: false, account_id: false, expiration_datetime: false, percentage_discount: false, fixed_discount: false };
  changed: Record<BooleanField | ValueField, boolean> = { is_active: false, one_time_use: false, trusted_only: false, coupons_max_usage: false, account_id: false, expiration_datetime: false, percentage_discount: false, fixed_discount: false };

  constructor(private route: ActivatedRoute, private router: Router, private couponService: CouponService) {}

  ngOnInit(): void {
    const ids = (this.route.snapshot.queryParamMap.get('ids') || '').split(',').map(Number).filter((id, index, values) => Number.isInteger(id) && id > 0 && values.indexOf(id) === index);
    if (ids.length < 2 || ids.length > 500) {
      this.feedbackError = 'Selecione entre 2 e 500 cupons para edição múltipla.';
      this.isLoading = false;
      return;
    }
    forkJoin(ids.map((id) => this.couponService.get(id))).subscribe({
      next: (coupons) => { this.coupons = coupons; this.syncValues(); this.isLoading = false; },
      error: () => { this.feedbackError = 'Não foi possível carregar os cupons selecionados.'; this.isLoading = false; },
    });
  }

  onBooleanChange(field: BooleanField, event: Event): void {
    this.booleanValues[field] = (event.target as HTMLInputElement).checked;
    this.changed[field] = true;
  }

  onValueChange(field: Exclude<ValueField, 'percentage_discount' | 'fixed_discount'>): void {
    this.changed[field] = true;
    this.hasDifferentValues[field] = false;
  }

  onDiscountChange(field: 'percentage_discount' | 'fixed_discount'): void {
    this.changed[field] = true;
    this.hasDifferentValues[field] = false;
    const other = field === 'percentage_discount' ? 'fixed_discount' : 'percentage_discount';
    this.changed[other] = false;
    this.hasDifferentValues[other] = false;
    this.bulkEdit[other] = null;
  }

  submit(): void {
    this.feedbackError = '';
    const payload: CouponBulkUpdatePayload = { ids: this.coupons.map((coupon) => coupon.id!) };
    (['is_active', 'one_time_use', 'trusted_only'] as BooleanField[]).forEach((field) => {
      if (this.changed[field]) payload[field] = this.booleanValues[field] as boolean;
    });
    if (this.changed.coupons_max_usage) payload.coupons_max_usage = this.bulkEdit.coupons_max_usage ?? 0;
    if (this.changed.account_id) payload.account_id = this.bulkEdit.account_id;
    if (this.changed.expiration_datetime) payload.expiration_datetime = this.bulkEdit.expiration_datetime ? new Date(this.bulkEdit.expiration_datetime).toISOString() : null;
    if (this.changed.percentage_discount) {
      if (!this.bulkEdit.percentage_discount) { this.feedbackError = 'Informe o valor do desconto em porcentagem.'; return; }
      payload.percentage_discount = this.bulkEdit.percentage_discount;
      payload.fixed_discount = null;
    }
    if (this.changed.fixed_discount) {
      if (!this.bulkEdit.fixed_discount) { this.feedbackError = 'Informe o valor do desconto fixo.'; return; }
      payload.fixed_discount = this.bulkEdit.fixed_discount;
      payload.percentage_discount = null;
    }
    if (Object.keys(payload).length === 1) { this.feedbackError = 'Altere ao menos um campo para continuar.'; return; }
    this.isSaving = true;
    this.couponService.bulkUpdate(payload).subscribe({
      next: () => this.router.navigate(['/dashboard/cupons']),
      error: () => { this.isSaving = false; this.feedbackError = 'Não foi possível atualizar os cupons selecionados.'; },
    });
  }

  private syncValues(): void {
    this.syncBoolean('is_active', this.coupons.map((coupon) => coupon.is_active ?? false));
    this.syncBoolean('one_time_use', this.coupons.map((coupon) => coupon.one_time_use ?? false));
    this.syncBoolean('trusted_only', this.coupons.map((coupon) => coupon.trusted_only ?? false));
    this.syncValue('coupons_max_usage', this.coupons.map((coupon) => coupon.coupons_max_usage ?? 0));
    this.syncValue('account_id', this.coupons.map((coupon) => coupon.account_id ?? coupon.account?.id ?? null));
    const expiration = this.getSharedValue(this.coupons.map((coupon) => coupon.expiration_datetime ?? null));
    this.bulkEdit.expiration_datetime = expiration ? this.toDatetimeLocal(expiration) : '';
    this.hasDifferentValues.expiration_datetime = expiration === undefined;
    this.syncValue('percentage_discount', this.coupons.map((coupon) => coupon.percentage_discount ?? null));
    this.syncValue('fixed_discount', this.coupons.map((coupon) => coupon.fixed_discount ?? null));
  }

  private syncBoolean(field: BooleanField, values: boolean[]): void {
    this.booleanValues[field] = this.getSharedValue(values) ?? 'mixed';
  }

  private syncValue(field: Exclude<ValueField, 'expiration_datetime'>, values: Array<number | null>): void {
    const value = this.getSharedValue(values);
    this.bulkEdit[field] = value ?? null;
    this.hasDifferentValues[field] = value === undefined;
  }

  private getSharedValue<T>(values: T[]): T | undefined {
    return values.length && values.every((value) => value === values[0]) ? values[0] : undefined;
  }

  private toDatetimeLocal(value: string): string {
    const date = new Date(value);
    const pad = (number: number) => String(number).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
}
